import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';

import {
  adminReviewErrorsSelector,
  getAdminEditReview,
  isLoadingAdminReviewSelector,
  selectedAdminReviewSelector,
  setAdminReviewErrors,
  setSelectedReview,
  updateAdminReview,
} from 'src/store/adminReviews';

import { CustomButton, CustomInput, CustomSelect, PageLoader, WhiteCard } from 'src/components';

import './adminReviewsForm.scss';

export const AdminReviewsForm = () => {
  const { t } = useTranslation('adminReviews');

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const params = useParams();

  const isEditMode = pathname?.includes('edit');

  const selectedReview = useSelector(selectedAdminReviewSelector);
  const isLoading = useSelector(isLoadingAdminReviewSelector);
  const formError = useSelector(adminReviewErrorsSelector);

  const statuses = [
    { value: 'PENDING', label: t(`statuses:PENDING`) },
    { value: 'COMPLETE', label: t(`statuses:COMPLETE`) },
    { value: 'CANCEL', label: t(`statuses:CANCEL`) },
  ];

  const [formData, setFormData] = useState({
    name: '',
    message: '',
    status: '',
    user: {
      login: '',
      email: '',
    },
  });

  useEffect(() => {
    if (isEditMode) {
      dispatch(getAdminEditReview(params?.id));
    }

    return () => {
      dispatch(setSelectedReview({}));
      dispatch(setAdminReviewErrors({}));
    };
  }, [dispatch, isEditMode, params?.id]);

  useEffect(() => {
    if (isEditMode) {
      setFormData((prev) => ({ ...prev, ...selectedReview }));
    }
  }, [isEditMode, selectedReview]);

  const onSubmit = useCallback(
    async (event) => {
      event.preventDefault();

      if (isEditMode) {
        await dispatch(updateAdminReview({ id: params?.id, payload: formData, navigate }));
      }
    },
    [dispatch, navigate, formData, isEditMode, params?.id],
  );

  const onChangeInput = useCallback(
    (key) => (event) => {
      setFormData((prev) => ({ ...prev, [key]: event.target.value }));

      const copiedErrors = _.cloneDeep(formError);

      delete copiedErrors[key];

      dispatch(setAdminReviewErrors(copiedErrors));
    },
    [dispatch, formError],
  );

  const onChangeSelect = useCallback(
    (key) => (event) => {
      setFormData((prev) => ({ ...prev, [key]: event.value }));

      const copiedErrors = _.cloneDeep(formError);

      delete copiedErrors[key];

      dispatch(setAdminReviewErrors(copiedErrors));
    },
    [dispatch, formError],
  );

  return (
    <div className="admin-reviews-form">
      <WhiteCard>
        {isLoading ? <PageLoader /> : null}

        <h3>{t(`adminReviews:${!isEditMode ? 'create' : 'edit'}`)}</h3>
        <form onSubmit={onSubmit}>
          <div className="admin-reviews-form__content">
            <CustomInput
              label={t('name')}
              value={formData?.name}
              onChange={onChangeInput('name')}
              errorMessage={formError?.name}
            />
            <CustomInput
              label={t('email')}
              value={formData?.user?.email}
              onChange={onChangeInput('rate_received_currency')}
              errorMessage={formError?.rate_received_currency}
              disabled
            />
          </div>
          <div className="admin-reviews-form__content__item">
            <CustomInput
              label={t('review')}
              value={formData?.message}
              onChange={onChangeInput('message')}
              type="text-area"
              disabled
            />
          </div>

          <div className="admin-reviews-form__content__item">
            <CustomSelect
              label={t('status')}
              value={formData?.status}
              onChange={onChangeSelect('status')}
              options={statuses}
            />
          </div>

          <CustomButton variant="filled" title={t('title:save')} />
        </form>
      </WhiteCard>
    </div>
  );
};
